import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Table, Input, Tag, Button, Typography, Space, Modal, message, Spin, Upload, Alert, Popconfirm, Descriptions, Select,
} from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageFilter from '../components/page/PageFilter';
import DataCard from '../components/page/DataCard';
import { UploadOutlined, InboxOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { Role } from '../constants/roles';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import {
    DASHBOARD_SORTER_TOOLTIP,
    DASHBOARD_TABLE_STICKY,
    dashboardPagination,
    questionBankTableScroll,
} from '../utils/adminDashboardTable';
import { sortByField, sortStatusField } from '../utils/tableSorters';
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
import '../styles/question-bank.css';

const { Text } = Typography;
const { Dragger } = Upload;

const DOMAIN_FILTERS = ['All', 'Organization', 'IT / IT enabled', 'People', 'Physical'];

/**
 * Question Bank — Database-backed GRC Control Assessment Library.
 * Supports CSV upload (drag-and-drop) and domain filtering.
 */
const QuestionBank = () => {
    const { user } = useApp();
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const isAdmin = user?.role === Role.ADMIN;

    const [questions, setQuestions] = useState([]);
    const [versions, setVersions] = useState([]);
    const [selectedVersionId, setSelectedVersionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingVersions, setLoadingVersions] = useState(true);
    const [error, setError] = useState(null);
    const [deletingVersion, setDeletingVersion] = useState(false);
    const [search, setSearch] = useState('');
    const [activeDomain, setActiveDomain] = useState('All');

    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    // Use refs to avoid MSAL objects causing infinite re-render loops
    const instanceRef = useRef(instance);
    const accountRef = useRef(activeAccount);
    useEffect(() => { instanceRef.current = instance; }, [instance]);
    useEffect(() => { accountRef.current = activeAccount; }, [activeAccount]);

    /** Get auth token (stable — uses refs) */
    const getToken = useCallback(async () => {
        const tokenResponse = await instanceRef.current.acquireTokenSilent({
            ...apiTokenRequest,
            account: accountRef.current,
        });
        return tokenResponse.accessToken;
    }, []);

    const fetchVersions = useCallback(async () => {
        try {
            setLoadingVersions(true);
            const token = await getToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/question-bank/versions`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || `HTTP ${res.status}`);
            const list = json.data.versions || [];
            setVersions(list);
            setSelectedVersionId((prev) => {
                if (prev && list.some((v) => v.id === prev)) return prev;
                return list.length ? list[list.length - 1].id : null;
            });
        } catch (err) {
            console.error('Failed to fetch versions:', err);
        } finally {
            setLoadingVersions(false);
        }
    }, [getToken]);

    /** Fetch questions for the selected snapshot */
    const fetchQuestions = useCallback(async () => {
        if (!selectedVersionId) {
            setQuestions([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const token = await getToken();
            const res = await fetch(
                `${apiConfig.baseUrl}/api/v1/questions?versionId=${encodeURIComponent(selectedVersionId)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || `HTTP ${res.status}`);
            setQuestions(json.data.questions || []);
        } catch (err) {
            console.error('Failed to fetch questions:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getToken, selectedVersionId]);

    useEffect(() => {
        fetchVersions();
    }, [fetchVersions]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    // Client-side filtering (search + domain)
    const filtered = questions.filter((q) => {
        const matchesSearch =
            !search ||
            q.question?.toLowerCase().includes(search.toLowerCase()) ||
            q.questionNo?.toLowerCase().includes(search.toLowerCase()) ||
            q.isoControl?.toLowerCase().includes(search.toLowerCase()) ||
            q.domain?.toLowerCase().includes(search.toLowerCase());
        const matchesDomain = activeDomain === 'All' || q.domain === activeDomain;
        return matchesSearch && matchesDomain;
    });

    // ── CSV Upload Handler ──────────────────────────────────────────────
    const handleCsvUpload = async (file) => {
        try {
            setUploading(true);
            setUploadResult(null);
            const token = await getToken();

            const formData = new FormData();
            formData.append('file', file);

            const res = await fetch(`${apiConfig.baseUrl}/api/v1/questions/upload-csv`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            const json = await res.json();

            if (!res.ok || !json.success) {
                setUploadResult({ type: 'error', message: json.error?.message || 'Upload failed' });
                message.error(json.error?.message || 'Upload failed');
            } else {
                setUploadResult({ type: 'success', ...json.data });
                const v = json.data.version;
                const d = json.data.diffVsPrevious;
                message.success(
                    v
                        ? `Created version ${v.label}: ${json.data.totalParsed} questions (+${d?.added ?? 0} / −${d?.removed ?? 0} / ~${d?.edited ?? 0} vs previous)`
                        : 'Upload complete'
                );
                await fetchVersions();
                if (v?.id) setSelectedVersionId(v.id);
            }
        } catch (err) {
            setUploadResult({ type: 'error', message: err.message });
            message.error('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
        }
        return false; // Prevent Ant default upload
    };

    const handleDeleteVersion = async () => {
        if (!selectedVersionId || !isAdmin) return;
        try {
            setDeletingVersion(true);
            const token = await getToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/question-bank/versions/${selectedVersionId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || res.statusText);
            message.success(`Deleted version ${json.data.label || ''}`);
            await fetchVersions();
        } catch (err) {
            message.error(err.message || 'Delete failed');
        } finally {
            setDeletingVersion(false);
        }
    };

    const selectedVer = useMemo(
        () => versions.find((v) => v.id === selectedVersionId),
        [versions, selectedVersionId]
    );

    const visibleVersions = useMemo(() => {
        let latest5;
        if (versions.length <= 5) {
            latest5 = [...versions];
        } else {
            latest5 = versions.slice(-5);
            if (!latest5.find(v => v.id === selectedVersionId)) {
                const selected = versions.find(v => v.id === selectedVersionId);
                if (selected) {
                    latest5 = [selected, ...latest5.slice(1)];
                }
            }
        }
        return latest5.reverse();
    }, [versions, selectedVersionId]);

    const olderVersions = useMemo(() => {
        if (versions.length <= 5) return [];
        return versions.filter(v => !visibleVersions.find(vis => vis.id === v.id)).reverse();
    }, [versions, visibleVersions]);

    // ── Table columns (resizable widths persisted per browser) ──────────
    const questionColumnsBase = useMemo(
        () => [
            {
                title: 'Q no.',
                dataIndex: 'questionNo',
                key: 'questionNo',
                width: 120,
                fixed: 'left',
                sorter: sortByField('questionNo'),
                render: (ref, record) => (
                    <Space size={6} wrap>
                        <Tag style={{ fontWeight: 700, fontSize: 12 }}>{ref}</Tag>
                        {record.changeVsPrevious === 'new' && (
                            <Tag color="green" style={{ fontSize: 10, fontWeight: 700, marginInlineEnd: 0 }}>New</Tag>
                        )}
                        {record.changeVsPrevious === 'edited' && (
                            <Tag color="blue" style={{ fontSize: 10, fontWeight: 700, marginInlineEnd: 0 }}>Edited</Tag>
                        )}
                    </Space>
                ),
            },
            {
                title: 'Control query',
                dataIndex: 'question',
                key: 'question',
                width: 500,
                sorter: sortByField('question'),
                render: (text, record) => (
                    <div>
                        <Text style={{ fontSize: 13, color: 'var(--color-text-hero)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {(text || '').replace(/\n/g, ' ')}
                        </Text>
                        <div style={{ marginTop: 6 }}>
                            {record.priority && <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>{record.priority}</Tag>}
                            <Tag style={{ fontSize: 10, fontWeight: 700 }}>Type: {record.responseType || 'N/A'}</Tag>
                            {record.mandatory === 'Y' && <Tag color="orange" style={{ fontSize: 10, fontWeight: 700 }}>Mandatory</Tag>}
                            {record.evidenceRequired === 'Y' && <Tag color="purple" style={{ fontSize: 10, fontWeight: 700 }}>Evidence req.</Tag>}
                        </div>
                    </div>
                ),
            },
            {
                title: 'Domain',
                dataIndex: 'domain',
                key: 'domain',
                width: 160,
                sorter: sortByField('domain'),
                render: (d) => (d ? <Tag color="cyan" style={{ fontWeight: 700, fontSize: 11 }}>{d}</Tag> : <Text type="secondary">-</Text>),
            },
            {
                title: 'ISO control',
                dataIndex: 'isoControl',
                key: 'isoControl',
                width: 120,
                sorter: sortByField('isoControl'),
                render: (target) => (target ? <Tag color="blue" style={{ fontWeight: 700, fontSize: 11 }}>{target}</Tag> : <Text type="secondary">-</Text>),
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                sorter: sortStatusField('status'),
                render: (status) => {
                    if (status == null || status === '') return <Text type="secondary">-</Text>;
                    const label = `${String(status).charAt(0)}${String(status).slice(1).toLowerCase()}`;
                    return <Tag color={status === 'ACTIVE' ? 'green' : 'default'} style={{ fontWeight: 700, fontSize: 11 }}>{label}</Tag>;
                },
            },
        ],
        []
    );

    const { columns: questionColumns, components: questionBankTableComponents } = useResizableTableColumns(
        'question-bank',
        questionColumnsBase
    );

    // ── Render ───────────────────────────────────────────────────────────
    return (
        <PageShell className="question-bank-view" width="wide">
            <PageHeader
                title="Question Bank"
                subtitle={`Global GRC Control Assessment Library${selectedVer ? ` - version ${selectedVer.label} - ${questions.length} questions in this snapshot` : ''}`}
                actions={(
                    <>
                    <Input.Search
                        placeholder="Search library..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 260 }}
                        allowClear
                    />
                    {isAdmin && (
                        <>
                            {selectedVer && versions.length > 0 && (
                                <Popconfirm
                                    title="Delete this version?"
                                    description="Removes this snapshot and all its questions. Other versions are unchanged."
                                    okText="Delete"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={handleDeleteVersion}
                                >
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                                        loading={deletingVersion}
                                        disabled={!selectedVersionId}
                                    >
                                        Delete version
                                    </Button>
                                </Popconfirm>
                            )}
                            <Button
                                icon={<UploadOutlined />}
                                onClick={() => { setIsUploadOpen(true); setUploadResult(null); }}
                            >
                                Upload CSV
                            </Button>
                        </>
                    )}
                    </>
                )}
            />

            {/* Version snapshots (v1, 1.1, 1.2, …) */}
            {versions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Versions</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <PageFilter
                            value={selectedVersionId}
                            onChange={setSelectedVersionId}
                            options={visibleVersions.map((v) => ({ label: `v${v.label}`, value: v.id }))}
                        />
                        {olderVersions.length > 0 && (
                            <Select
                                key={selectedVersionId}
                                value={undefined}
                                onChange={setSelectedVersionId}
                                placeholder={<span><HistoryOutlined style={{ marginRight: 6 }} /> Older versions</span>}
                                options={olderVersions.map((v) => ({ label: `v${v.label}`, value: v.id }))}
                                size="large"
                                style={{ minWidth: 160 }}
                                dropdownMatchSelectWidth={false}
                            />
                        )}
                    </div>
                </div>
            )}

            {selectedVer && (
                <DataCard size="small">
                    <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} title={`Snapshot ${selectedVer.label}`}>
                        <Descriptions.Item label="Uploaded">
                            {selectedVer.uploadedAt ? formatDdMmYyyyTime(selectedVer.uploadedAt) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Total questions">{selectedVer.totalQuestions}</Descriptions.Item>
                    </Descriptions>
                    {selectedVer.label !== '1' && (
                        <div style={{ marginTop: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                vs previous snapshot
                            </Text>
                            <Text style={{ fontSize: 13 }}>
                                <Tag color="green">+{selectedVer.vsPreviousAdded} new</Tag>{' '}
                                <Tag color="red">−{selectedVer.vsPreviousRemoved} removed</Tag>{' '}
                                <Tag color="blue">~{selectedVer.vsPreviousEdited} edited</Tag>
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10, marginBottom: 4 }}>
                                vs version 1 (baseline)
                            </Text>
                            <Text style={{ fontSize: 13 }}>
                                <Tag color="green">+{selectedVer.vsBaselineAdded} new</Tag>{' '}
                                <Tag color="red">−{selectedVer.vsBaselineRemoved} removed</Tag>{' '}
                                <Tag color="blue">~{selectedVer.vsBaselineEdited} edited</Tag>
                            </Text>
                        </div>
                    )}
                </DataCard>
            )}

            {/* Domain Tabs */}
            <PageFilter
                value={activeDomain}
                onChange={setActiveDomain}
                options={DOMAIN_FILTERS.map((d) => ({ value: d, label: d }))}
            />

            {/* Error State */}
            {error && (
                <Alert
                    type="error"
                    message="Failed to load questions"
                    description={error}
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* Table */}
            <DataCard className="policylens-table-card">
                <Table
                    className="policylens-data-table"
                    dataSource={filtered}
                    columns={questionColumns}
                    components={questionBankTableComponents}
                    rowKey="id"
                    rowClassName={(record) => {
                        if (record.changeVsPrevious === 'new') return 'question-bank-row--new';
                        if (record.changeVsPrevious === 'edited') return 'question-bank-row--edited';
                        return '';
                    }}
                    loading={loading}
                    pagination={dashboardPagination({
                        pageSize: 15,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} questions`,
                    })}
                    size="middle"
                    sticky={DASHBOARD_TABLE_STICKY}
                    scroll={questionBankTableScroll()}
                    tableLayout="fixed"
                    showSorterTooltip={DASHBOARD_SORTER_TOOLTIP}
                    style={{ color: 'var(--color-text-hero)' }}
                    locale={{ emptyText: loading ? 'Loading...' : 'No questions found. Upload a CSV to get started.' }}
                />
            </DataCard>

            {/* ── Upload CSV Modal ────────────────────────────────────── */}
            <Modal
                title="Upload Question Bank CSV"
                open={isUploadOpen}
                onCancel={() => setIsUploadOpen(false)}
                footer={null}
                width={560}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                        Each upload creates a <strong>new snapshot</strong> (version 1, then 1.1, 1.2, …) containing the full CSV.
                        Columns: Q No., Question, Response Type, Priority, Mandatory, Evidence Required, Expected Respondent, Domain, ISO Control.
                    </Text>
                </div>

                <Dragger
                    accept=".csv"
                    maxCount={1}
                    showUploadList={false}
                    customRequest={({ file }) => handleCsvUpload(file)}
                    disabled={uploading}
                    style={{ padding: '20px 0' }}
                >
                    {uploading ? (
                        <div>
                            <Spin size="large" />
                            <p style={{ marginTop: 12 }}>Processing CSV...</p>
                        </div>
                    ) : (
                        <div>
                            <p className="ant-upload-drag-icon"><InboxOutlined style={{ fontSize: 48, color: 'var(--color-accent)' }} /></p>
                            <p className="ant-upload-text" style={{ fontWeight: 700 }}>Click or drag CSV file here</p>
                            <p className="ant-upload-hint">Supports .csv files up to 5MB</p>
                        </div>
                    )}
                </Dragger>

                {uploadResult && (
                    <Alert
                        style={{ marginTop: 16 }}
                        type={uploadResult.type === 'success' ? 'success' : 'error'}
                        showIcon
                        message={uploadResult.type === 'success'
                            ? (uploadResult.version
                                ? `Version ${uploadResult.version.label} - ${uploadResult.totalParsed} questions`
                                : 'Upload complete')
                            : `Upload Failed: ${uploadResult.message}`
                        }
                    />
                )}
            </Modal>
        </PageShell>
    );
};

export default QuestionBank;
